import React from 'react';
import { ClerkProvider, SignIn, SignedIn, SignedOut } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import queryClient from '../../../queryClient';
import { buildClerkAppearance } from 'modules/Common/config/clerkAppearance';
import { useBranding } from 'modules/Common/hooks/useBranding';
import AdminShell from './AdminShell';
import OrganizationsPage from '../pages/OrganizationsPage';
import ErpDemandPage from '../pages/ErpDemandPage';

// Root of the admin console. The provider stack is the one phase 3 shipped; phase 4
// replaced the route target only.
//
// Order is load-bearing: useCgTable calls useHistory/useLocation unconditionally,
// so any table must sit UNDER BrowserRouter — even with syncUrl off. Lifting a
// table above the router throws.
//
// It lives in components/ rather than at the module root because rule 6 of
// scripts/lint-structure.js is a per-module ratchet on module-root flat files
// and new modules start at a budget of zero: src/modules/Admin/AdminRoot.jsx
// would fail `npm run lint` on its first commit.

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Fail at boot, not at the first sign-in attempt. ClerkProvider throws its own
// error for a missing key, but only once React reaches it — inside the error
// boundary, so it surfaces as a generic crash page. This names the variable.
if (!clerkPubKey) {
  throw new Error(
    'REACT_APP_CLERK_PUBLISHABLE_KEY is not set — the admin console cannot authenticate. ' +
      'Check that the .env for this environment was copied into the image.',
  );
}

export default function AdminRoot() {
  // The same appearance the customer auth pages use. Without it a whitelabel
  // admin gets a generic Clerk card in front of a branded console — the one
  // screen where the product's identity is least established is the one that
  // would have shown none of it.
  const brand = useBranding();

  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <QueryClientProvider client={queryClient}>
        <SignedIn>
          <BrowserRouter>
            <AdminShell>
              <Switch>
                <Route
                  exact
                  path="/"
                >
                  <OrganizationsPage />
                </Route>
                <Route path="/erp-demand">
                  <ErpDemandPage />
                </Route>
                {/* React Router v5, matching the rest of the repo. */}
                <Route path="*">
                  <Redirect to="/" />
                </Route>
              </Switch>
            </AdminShell>
          </BrowserRouter>
        </SignedIn>
        {/*
          The sign-in card is RENDERED here, not redirected to.

          RedirectToSignIn was an infinite loop: with no `signInUrl` on
          ClerkProvider, Clerk defaults it to `/sign-in` on the CURRENT origin —
          and this app serves admin.html for every path (SPA fallback), so
          /sign-in mounts AdminRoot again, is still signed out, and redirects
          again. Each hop nests the previous URL as `redirect_url`, so the
          address bar grows without bound and the page stays blank.

          Rendering the component instead means nothing navigates, so no loop is
          possible regardless of what signInUrl would have resolved to — which
          also sidesteps the account-portal-vs-custom-domain difference between
          development and production instances.

          routing="hash" because this is not mounted under a dedicated path
          route: it renders for EVERY path while signed out, and Clerk's
          multi-step flows (2FA, password reset) would otherwise demand real
          sub-paths.

          forceRedirectUrl="/" and not `fallbackRedirectUrl`: a redirect_url in
          the query is exactly what the loop was accumulating, and there is
          nowhere in this console to deep-link to anyway.

          withSignUp={false} because admin access is granted by a CgAdmin row
          (backend phase 1), never by self-service — an admin console that
          offers to create you an account is a contradiction.
        */}
        <SignedOut>
          <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
            <SignIn
              routing="hash"
              forceRedirectUrl="/"
              withSignUp={false}
              appearance={buildClerkAppearance(brand)}
            />
          </div>
        </SignedOut>
      </QueryClientProvider>
    </ClerkProvider>
  );
}
